import _ from "lodash";
import { normalizeString } from "@/js/utils";
import { objectVariable } from "@/js/document/document";

/**
 *
 * @param input
 * an object with properties
 * @returns Object
 * return a new Object with only the Array and Object properties type witch are not empty as value
 */

export function getPropsOfObjectTypes(input) {
  const keys = _.keys(input);
  const values = _.values(input);
  let rez = {};
  for (let i = 0; i < values.length; i++) {
    if (
      !_.isEmpty(values[i]) &&
      (_.isArray(values[i]) || _.isPlainObject(values[i]))
    ) {
      rez[keys[i]] = values[i];
    }
  }
  return rez;
}

/**
 *
 * @param input
 * an object with properties
 * @returns Object
 * return a new Array with object key/value with only primitive types witch are not empty
 */

export function getPropsOfPrimitiveTypes(input) {
  const keys = _.keys(input);
  const values = _.values(input);
  let rez = [];
  for (let i = 0; i < values.length; i++) {
    if (
      !_.isEmpty(values[i]) &&
      !_.isArray(values[i]) &&
      !_.isPlainObject(values[i])
    ) {
      let obj = {};
      obj.key = keys[i];
      obj.value = values[i];
      rez.push(obj);
    }
  }
  return rez;
}

/**
 *
 * @param input // array
 * @param propertyName // array item property name to lookup
 * @param propertyValue // array item property value to lookup
 * @param childrenPropertyName // `children` or something else
 * @returns {boolean|*}
 */
export function getTreeItemDirectChildren(input, propertyName, propertyValue, childrenPropertyName) {
  if (!(propertyName && propertyValue && childrenPropertyName)) return false

  if (Array.isArray(input)) {
    for (let elem of input) {
      if (elem[propertyName] && elem[propertyName] === propertyValue) {
        return elem[childrenPropertyName]
      } else if (elem[childrenPropertyName]) {
        getTreeItemDirectChildren(elem[childrenPropertyName], propertyName, propertyValue, childrenPropertyName)
      }
    }
  } else {
    return false
  }
}

/**
 *
 * @param collection, collection of points
 * @param point, point from map on click
 * @return index of the point found in the point collection
 */

export async function idxOfPointInPoints(collection, point) {
  const turf = await import('@turf/turf');
  const targetPoint = turf.point([point.lng, point.lat]);
  const points = [];
  for (let x = 0; x < collection.length; x++) {
    points.push(turf.point(collection[x]));
  }
  const nearest = turf.nearestPoint(
    targetPoint,
    turf.featureCollection(points)
  );

  const searchJson = JSON.stringify(nearest.geometry.coordinates);
  const arrJson = collection.map((e) => JSON.stringify(e));
  return arrJson.indexOf(searchJson);
}

/**
 *
 * @param input // array
 * @param propertyName // array item property name to lookup
 * @param propertyValue // array item property value to lookup
 * @param childrenPropertyName // `children` or something else
 * @returns {boolean|*}
 */
export function getTreeItemValue(input, propertyName, propertyValue, childrenPropertyName) {
  if (!(propertyName && propertyValue && childrenPropertyName)) return false

  if (Array.isArray(input)) {
    for (let elem of input) {
      if (/\./.test(propertyName)) {
        elem[propertyName] = objectVariable(elem, propertyName)
      }
      if (elem[propertyName] && elem[propertyName] === propertyValue) {
        return elem
      } else if (elem[childrenPropertyName]) {
        const search = getTreeItemValue(elem[childrenPropertyName], propertyName, propertyValue, childrenPropertyName)
        if (search) return search
      }
    }
  } else {
    return false
  }
}

/**
 * Filter nested array and return nested array with filtered children, including parents
 * @param input
 * @param query
 * @param childrenKey
 * @param searchField
 * @param exactMatch
 * @param result
 * @param parent
 * @returns {*}
 */
export function filterNestedArray(input, query, childrenKey, searchField, exactMatch, result, parent) {
  if (!query) {
    return input
  }
  if (Array.isArray(input) && input.length) {
    for (let item of input) {
      let children = []
      if (item[childrenKey] && item[childrenKey].length)
        children = filterNestedArray(item[childrenKey], query, childrenKey, searchField, exactMatch, [])
      if (children && children.length)
        item[childrenKey] = children
      else delete item[childrenKey];

      const value = item[searchField] || searchField.interpolateObject(item);

      try {
        if (
          (exactMatch && normalizeString(value) === query) ||
          (!exactMatch && new RegExp(query, 'gi').test(normalizeString(value))) ||
          (item[childrenKey] && item[childrenKey].length)
        ) {
          result.push(item);
        }
      } catch (e) {}
      
    }
  }
  return result
}

export function multipleFilterNestedArray(input, query, childrenKey, searchFields, result) {
  if (!query) {
    return input
  }
  if (Array.isArray(input) && input.length) {
    for (let item of input) {
      let children = []
      if (item[childrenKey] && item[childrenKey].length)
        children = multipleFilterNestedArray(item[childrenKey], query, childrenKey, searchFields, [])
      if (children && children.length)
        item[childrenKey] = children
      else delete item[childrenKey];
      if (
        searchFields.filter(searchField => new RegExp(query, 'gi').test(normalizeString(item[searchField]))).length ||
        (item[childrenKey] && item[childrenKey].length)
      ) {
        result.push(item);
      }
    }
  }
  return result
}

export function removeCircular(input) {
  const ancestors = new WeakSet();
  function isPlainObject(val) {
    return (
      val !== null &&
      typeof val === 'object' &&
      Object.getPrototypeOf(val) === Object.prototype
    );
  }
  function clone(value) {
    if (value === null || typeof value !== 'object') {
      if (typeof value === 'function') return undefined;
      return value;
    }
    if (ancestors.has(value)) {
      return undefined;
    }
    if (!Array.isArray(value) && !isPlainObject(value)) {
      return undefined;
    }
    ancestors.add(value);
    let result;
    if (Array.isArray(value)) {
      result = value
        .map(clone)
        .filter((x) => x !== undefined);
    } else {
      result = {};
      for (const [k, v] of Object.entries(value)) {
        const c = clone(v);
        if (c !== undefined) {
          result[k] = c;
        }
      }
    }
    ancestors.delete(value);
    return result;
  }
  return clone(input);
}

export function objectToTree(obj, prefix = '') {
  const cleanObj = removeCircular(obj);

  function traverse(value, path = [], keyPrefix = '0') {
    return Object.entries(value).map(([key, val], index) => {
      const newPath = [...path, key];
      const keyPath = keyPrefix + '-' + index;
      const isObject = val && typeof val === 'object' && !Array.isArray(val);

      const item = {
        key: path.length === 0 ? String(index) : keyPath,
        label: key,
        metaLabel: newPath.join(' > '),
        data: newPath.reduce((acc, part) => {
          const accessor = /^[a-zA-Z_$][\w$]*$/.test(part)
            ? `${acc || prefix ? '.' : ''}${part}`
            : `['${part}']`;
          return acc + accessor;
        }, prefix)
      };

      if (isObject) {
        item.children = traverse(val, newPath, keyPath);
      }

      return item;
    });
  }

  return cleanObj ? traverse(cleanObj) : [];
}

export function matchesRule(object, rule) {
  const fieldValue = eval(`object.${rule.field}`);
  switch (rule.operator) {
      case 'contains':
          return String(fieldValue).includes(String(rule.value));
      case 'notContains':
          return !String(fieldValue).includes(String(rule.value));
      case 'startsWith':
          return String(fieldValue).startsWith(String(rule.value));
      case 'endsWith':
          return String(fieldValue).endsWith(String(rule.value));
      case "equals":
          return fieldValue === rule.value;
      case "notEquals":
          return fieldValue !== rule.value;
      case "greaterThan":
          return fieldValue > rule.value;
      case "greaterThanOrEqual":
          return fieldValue >= rule.value;
      case "lessThan":
          return fieldValue < rule.value;
      case "lessThanOrEqual":
          return fieldValue <= rule.value;
      case "isNull":
          return [null, undefined].includes(fieldValue);
      case "isNotNull":
          return ![null, undefined].includes(fieldValue);
      default:
          return false;
  }
}

export function evaluateRules(rules, object, operator) {
  return rules.reduce((acc, rule) => {
    const result = rule.type === "group"
        ? evaluateRules(rule.rules, object, rule.operator)
        : matchesRule(object, rule);
    return operator === "AND" ? acc && result : acc || result;
  }, operator === "AND");
}
